import { useEffect, useRef } from 'react';
import { useAppSelector } from '../../../../flux/stores/hooks';
import { useFolderExpand } from '../../../../utils/useFolderExpand';
import { ModelRow } from '../ModelRow/ModelRow';
import './FolderTreeNode.css';

/**
 * One folder in the ACC tree, expanded lazily on click. Both its subfolders
 * and its direct RVT files come from the same walk (`useFolderExpand`) over
 * `GET /aps/folders/{urn}/contents?type=folder,rvt` — the folder browser is
 * the source of truth for what's in ACC, never `/aps/rvt-models`.
 */
export const FolderTreeNode = ({
    projectId,
    folderUrn,
    depth,
    expandedSet,
    onToggleExpand,
    ...rowProps
}) => {
    const node = useAppSelector(state => state.projects.aps[projectId]?.folders.byUrn[folderUrn]);
    const byUrn = useAppSelector(state => state.projects.aps[projectId]?.folders.byUrn);
    const requestedRef = useRef(false);
    const expandFolder = useFolderExpand(projectId);
    const expanded = expandedSet.has(folderUrn);

    useEffect(() => {
        if (!expanded || !node) return;
        if (node.subfolders !== null || node.status === 'loading' || node.status === 'error') return;
        if (requestedRef.current) return;
        requestedRef.current = true;
        expandFolder(folderUrn);
    }, [expanded, node, folderUrn, expandFolder]);

    if (!node) return null;

    const sortedSubfolders = [...(node.subfolders ?? [])].sort((a, b) =>
        (byUrn?.[a]?.name ?? '').localeCompare(byUrn?.[b]?.name ?? '')
    );
    const sortedModels = [...(node.models ?? [])].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    const stillDiscovering = expanded && node.status === 'loading';
    const isEmpty = expanded && node.status === 'loaded' && sortedSubfolders.length === 0 && sortedModels.length === 0;

    return (
        <div className="mg-tree-node" style={{ marginLeft: `${depth * 18}px` }}>
            <button
                type="button"
                className="mg-tree-folder"
                onClick={() => onToggleExpand(folderUrn)}
                aria-expanded={expanded}
            >
                <span className={`mg-tree-chevron${expanded ? ' mg-tree-chevron-open' : ''}`} aria-hidden="true" />
                <span className="mg-tree-folder-icon" aria-hidden="true" />
                {node.name ?? 'Untitled folder'}
            </button>

            {expanded && (
                <div className="mg-tree-children">
                    {node.status === 'error' && (
                        <p className="org-error">This folder is no longer available — refresh.</p>
                    )}
                    {sortedSubfolders.map(childUrn => (
                        <FolderTreeNode
                            key={childUrn}
                            projectId={projectId}
                            folderUrn={childUrn}
                            depth={depth + 1}
                            expandedSet={expandedSet}
                            onToggleExpand={onToggleExpand}
                            {...rowProps}
                        />
                    ))}
                    {sortedModels.map(entry => (
                        <ModelRow key={entry.id} lineageUrn={entry.id} name={entry.name} {...rowProps} />
                    ))}
                    {stillDiscovering && <p className="mg-tree-loading-more">Loading…</p>}
                    {isEmpty && <p className="mg-tree-empty">Nothing here.</p>}
                </div>
            )}
        </div>
    );
};
